// API wrappers for Community Management — external community websites a brand
// engages on (Skool, Quora, Reddit, Discord, niche forums, …). A first-class
// side-menu feature with a Prospects→Active lifecycle, per-brand tagging and
// favourites. The backend route is mounted at /api/communities and is gated
// by community* permissions. Secrets (login password, apiKey) are never
// returned in the list/get shape; the server surfaces `_hasPassword` /
// `_hasApiKey` flags and a dedicated /credentials endpoint for manual login.
//
// Every wrapper is a thin pass-through to the shared fetch helpers in
// ../index so it can be unit-tested by mocking that module.
import { post, get, globalPut, remove } from "../index";

const BASE = "/api/communities";

function qs(params = {}) {
  const parts = [];
  for (const k in params) {
    if (params[k] === undefined || params[k] === null || params[k] === "") continue;
    parts.push(`${k}=${encodeURIComponent(params[k])}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

// ---- CRUD ----------------------------------------------------------------

// List communities, optionally filtered by brand + status (prospect|active).
export const listCommunities = (params) => get(`${BASE}${qs(params)}`);

export const getCommunity = (id) => get(`${BASE}/${id}`);

export const createCommunity = (data) => post(BASE, data, false);

export const updateCommunity = (id, data) =>
  globalPut(`${BASE}/${id}`, data, false);

export const deleteCommunity = (id) => remove(`${BASE}/${id}`);

// Toggle the favourite flag (mirrors brands/ideas favourite behaviour).
export const toggleFavourite = (id, favourite) =>
  globalPut(`${BASE}/${id}`, { favourite }, false);

// Decrypted credentials for manual sign-in.
export const getCommunityCredentials = (id) => get(`${BASE}/${id}/credentials`);

// ---- Lifecycle + engagement actions (worker-backed) ----------------------

// "Discover now" — internet-wide discovery of prospect communities for a
// brand (the weekly scheduler also does this when auto-community is on).
export const discoverCommunities = (brandId) =>
  post(`${BASE}/discover`, { brand_id: brandId }, false);

// Approve a prospect → active. The worker registers/subscribes the brand and
// seeds the active post list.
export const approveCommunity = (id) => post(`${BASE}/${id}/approve`, {}, false);

// Ask the worker for an AI-drafted reply to an active post. Creates a pending
// reply-history row and returns its replyId; the worker fills in the text.
export const requestAiReply = (id, body) =>
  post(`${BASE}/${id}/ai-reply`, body, false);

// Edit a draft reply's text/image before sending.
export const updateReply = (id, replyId, body) =>
  globalPut(`${BASE}/${id}/replies/${replyId}`, body, false);

// Send a reviewed reply — the worker posts it to the platform and keeps it
// in history for monitoring.
export const sendReply = (id, replyId, body) =>
  post(`${BASE}/${id}/replies/${replyId}/send`, body || {}, false);

// Reply history (the monitoring tab).
export const getReplyHistory = (id) => get(`${BASE}/${id}/history`);

// ---- Live messaging connectors + live interaction ------------------------

// The per-channel connector catalog (WhatsApp/Discord/Telegram/Signal): the
// fields + secrets the operator must supply, and which app-level platform
// credentials are configured. Drives the "Connect" form.
export const getConnectorCatalog = () => get(`${BASE}/connectors/catalog`);

// Wire a live connector to an Active community. `body` is
// { channel, config:{...}, secrets:{...} }. On missing fields the server
// returns 400 with `missing` so the FE can ask the operator for them.
export const connectChannel = (id, body) =>
  post(`${BASE}/${id}/connector/connect`, body, false);

// Drop the live connection (keeps the card).
export const disconnectChannel = (id) =>
  post(`${BASE}/${id}/connector/disconnect`, {}, false);

// The live interaction thread (oldest-first).
export const getMessages = (id) => get(`${BASE}/${id}/messages`);

// Send a live message through the connector.
export const sendMessage = (id, text, to) =>
  post(`${BASE}/${id}/messages`, { text, to }, false);
